import React, { useState, useEffect, useRef } from 'react';
import { useScoreboard } from './context/ScoreboardContext';
import ImageCarousel from './ImageCarousel';
import { playBuzzer } from './sounds/buzzer';

const BreakOverlay = ({ isLiveView }) => {
  const {
    showBreakOverlay,
    setShowBreakOverlay,
    breakTimeInSeconds,
    setBreakTimeInSeconds,
    isBreakActive,
    setIsBreakActive,
    centralTick,
    carouselImages,
    autoBreakEnabled
  } = useScoreboard();

  const [timeDisplay, setTimeDisplay] = useState('10:00');
  const [isSmallOverlay, setIsSmallOverlay] = useState(false);
  const breakStartTime = useRef(0);
  const initialBreakTime = useRef(0);
  const prevBreakTimeRef = useRef(null);

  const handleStopBreak = () => {
    setIsBreakActive(false);
    setShowBreakOverlay(false);
    // Reset refs and state when break ends
    breakStartTime.current = 0;
    setIsSmallOverlay(false);
  };

  useEffect(() => {
    if (isBreakActive && breakTimeInSeconds > 0) {
      if (breakStartTime.current === 0) {
        breakStartTime.current = Date.now();
        initialBreakTime.current = breakTimeInSeconds;
        // Set initial small overlay state for autobreak
        console.log('BreakOverlay: autoBreakEnabled =', autoBreakEnabled);
        if (autoBreakEnabled) {          
          setIsSmallOverlay(true);
        }
      }
      
      const elapsedSeconds = Math.floor((Date.now() - breakStartTime.current) / 1000);
      const newTime = Math.max(0, initialBreakTime.current - elapsedSeconds);
      
      // Handle 30-second transition for autobreak
      if (autoBreakEnabled && isSmallOverlay && elapsedSeconds >= 30) {
        setIsSmallOverlay(false);
      }
      
      if (newTime !== breakTimeInSeconds) {
        setBreakTimeInSeconds(newTime);
      }

      const minutes = Math.floor(newTime / 60);
      const seconds = newTime % 60;
      setTimeDisplay(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);

      // Play buzzer when break timer reaches 0
      if (prevBreakTimeRef.current !== null && prevBreakTimeRef.current > 0 && newTime === 0) {
        playBuzzer();
      }
      prevBreakTimeRef.current = newTime;

      if (newTime === 0) {
        handleStopBreak();
      }
    }
  }, [centralTick, isBreakActive, breakTimeInSeconds, setBreakTimeInSeconds, setIsBreakActive, setShowBreakOverlay, autoBreakEnabled, isSmallOverlay]);

  if (!showBreakOverlay) return null;

  return (
    <>
      {isLiveView ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Small overlay for first 30 seconds of autobreak */}
          {autoBreakEnabled && isSmallOverlay ? (
            <div className="bg-white rounded-lg w-1/3 max-w-[60vw] py-6 px-6 flex flex-col items-center">
              <h2 className="font-bold text-black mb-4 text-[4vh]">
                Break Time
              </h2>
              <div className={`font-bold text-[12vh] ${breakTimeInSeconds <= 15 ? 'text-red-500' : 'text-black'}`}>
                {timeDisplay}
              </div>              
            </div>
          ) : (
            /* Full overlay with sponsors */
            <div className="bg-white rounded-lg w-3/5 max-w-[90vw] py-10 px-8 flex flex-col items-center">
              <h2 className="font-bold text-black mb-6 text-[8vh]">
                Break Time
              </h2>
              <div className={`font-bold mb-6 text-[20vh] ${breakTimeInSeconds <= 15 ? 'text-red-500' : 'text-black'}`}>
                {timeDisplay}
              </div>
              <div className="w-full h-[32vh]">
                <ImageCarousel isLiveView={true} images={carouselImages} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="fixed bottom-16 left-4 w-[180px] flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full p-3 flex flex-col items-center">
            <h2 className="font-bold text-black mb-4 text-lg">
              Break Time
            </h2>
            <div className={`font-bold mb-4 text-3xl ${breakTimeInSeconds <= 15 ? 'text-red-500' : 'text-black'}`}>
              {timeDisplay}
            </div>
            <button
              onClick={handleStopBreak}
              className="px-4 py-2 bg-amber-500 text-black text-base font-bold rounded-lg hover:bg-amber-600 w-full"
            >
              End Break
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BreakOverlay;
